import { useCallback, useEffect, useMemo, useState } from "react";
import { useTabActive } from "@/layouts/adminTabs/tabRuntime";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { Calendar, ChefHat, FileSpreadsheet, FileText, GripVertical, Loader2, Package, Tag } from "lucide-react";
import { OrderLabelsDialog } from "@/components/admin/OrderLabelsDialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { fetchAdminOrderSummaries, fetchAdminOrder, transitionOrder } from "@/lib/ordersApi";
import { downloadProductionReportExport } from "@/lib/adminProductionReportApi";
import { getApiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import type { OrderDetail, OrderProductionListItem, OrderSummary, ProductionItemStatus } from "@/lib/types";
import { hasPermission } from "@/lib/types";
import { CLIENT_TYPE_LABELS } from "@/lib/clientTypeLabels";
import { ProductionOrderDialog } from "@/components/admin/ProductionOrderDialog";
import { AdvanceConfirmDialog } from "@/components/admin/AdvanceConfirmDialog";
import {
  KanbanDateFilterBar,
  resolveFilterDate,
  type KanbanDateFilter,
} from "@/components/admin/KanbanDateFilter";
import { orderTitle } from "@/lib/orderUtils";
import { useApp } from "@/store/useApp";

const POLL_INTERVAL_MS = 15_000;

type SubStatus = "PARA_PRODUZIR" | "PRODUZINDO" | "PRODUZIDO";

interface ColumnDef {
  id: SubStatus;
  label: string;
  dotColor: string;
  cardBg: string;
  cardBorder: string;
}

const COLUMNS: ColumnDef[] = [
  { id: "PARA_PRODUZIR", label: "Para produzir",         dotColor: "bg-muted-foreground/60", cardBg: "bg-surface-elevated", cardBorder: "border-border" },
  { id: "PRODUZINDO",    label: "Produzindo",            dotColor: "bg-warning",             cardBg: "bg-warning/5",        cardBorder: "border-warning/30" },
  { id: "PRODUZIDO",     label: "Produzido / Em expedição", dotColor: "bg-success",          cardBg: "bg-success/5",        cardBorder: "border-success/30" },
];

const DECIDED_STATUSES: ProductionItemStatus[] = ["PRONTO", "PARCIAL", "SEM_MERCADORIA", "CANCELADO"];

function isDecided(item: OrderProductionListItem): boolean {
  return DECIDED_STATUSES.includes(item.status);
}

type OrderWithDecisions = OrderSummary & {
  decided: number;
  total: number;
  phase: "doing" | "done";
};

// Data de produção (yyyy-MM-dd) — mesma chave do relatório de produção:
// productionScheduledAt e, na falta dela, purchaseStartedAt. deliveryDate é o
// último recurso só pra não sumir do board.
function productionDateOf(order: OrderSummary): string | undefined {
  const src = order.productionScheduledAt ?? order.purchaseStartedAt;
  if (src) return src.slice(0, 10);
  return order.deliveryDate;
}

// yyyy-MM-dd → dd/MM
function shortBR(iso?: string): string | undefined {
  if (!iso) return undefined;
  const [, m, d] = iso.split("-");
  return m && d ? `${d}/${m}` : undefined;
}

function downloadBlob(filename: string, blob: Blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function subStatusFor(order: OrderWithDecisions): SubStatus {
  if (order.phase === "done") return "PRODUZIDO";
  // Pedido EM_PRODUCAO que já teve expedição agendada só pode ter voltado da expedição:
  // reentra como "Produzindo" (rework), mesmo que os itens ainda estejam todos decididos.
  if (order.expeditionScheduledAt) return "PRODUZINDO";
  // Pedido sem itens processados (todos in natura) já está pronto para expedição.
  if (order.total === 0) return "PRODUZIDO";
  if (order.decided === 0) return "PARA_PRODUZIR";
  if (order.decided === order.total) return "PRODUZIDO";
  return "PRODUZINDO";
}

export default function DashboardProducao() {
  // O kanban só é movimentável (avanço de etapa de produção) por quem tem PEDIDOS.PRODUCAO.AVANCAR.
  // Quem tem apenas IN_NATURA visualiza o board e decide itens in natura pelo diálogo (sem avançar).
  const readOnly = !hasPermission(useApp((s) => s.account), "PEDIDOS.PRODUCAO.AVANCAR");
  const canGenerateLabels = hasPermission(useApp((s) => s.account), "ETIQUETAS.ETIQUETA.GERAR");
  const [orders, setOrders] = useState<OrderWithDecisions[]>([]);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "ready" | "error">("idle");
  const [error, setError] = useState<string | null>(null);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [labelOrderId, setLabelOrderId] = useState<string | null>(null);
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const [dateFilter, setDateFilter] = useState<KanbanDateFilter>({ kind: "today" });
  const [advanceTarget, setAdvanceTarget] = useState<OrderWithDecisions | null>(null);
  const [overrides, setOverrides] = useState<Record<string, SubStatus>>({});
  const [exporting, setExporting] = useState<"pdf" | "xlsx" | null>(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const loadAll = useCallback(async (silent = false) => {
    if (!silent) {
      setLoadState("loading");
      setError(null);
    }
    try {
      const summaries = await fetchAdminOrderSummaries();
      // EM_PRODUCAO = ainda nas mãos da produção. EM_EXPEDICAO = já foi pra
      // expedição; mantém no board como confirmação até passar pra EM_ROTA.
      const inScope = summaries.filter(
        (o) => o.status === "EM_PRODUCAO" || o.status === "EM_EXPEDICAO",
      );
      const detailed = await Promise.all(
        inScope.map(async (summary) => {
          const phase: "doing" | "done" = summary.status === "EM_PRODUCAO" ? "doing" : "done";
          if (phase === "done") {
            return { ...summary, decided: 0, total: 0, phase };
          }
          try {
            const detail = await fetchAdminOrder(summary.id);
            const total = detail.productionList.length;
            const decided = detail.productionList.filter(isDecided).length;
            return { ...summary, decided, total, phase };
          } catch {
            return { ...summary, decided: 0, total: 0, phase };
          }
        }),
      );
      setOrders(detailed);
      setLoadState("ready");
    } catch (err) {
      if (!silent) {
        setError(getApiErrorMessage(err, "Não foi possível carregar os pedidos"));
        setLoadState("error");
      }
    }
  }, []);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  // Pausa o auto-refresh quando a aba interna está inativa (keep-alive); retoma ao reativar, sem duplicar.
  const tabActive = useTabActive();

  useEffect(() => {
    if (selectedOrderId || !tabActive) return;
    const tick = () => {
      if (document.visibilityState === "visible") void loadAll(true);
    };
    const id = window.setInterval(tick, POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [loadAll, selectedOrderId, tabActive]);

  const filteredOrders = useMemo(() => {
    const target = resolveFilterDate(dateFilter);
    if (!target) return orders;
    return orders.filter((o) => productionDateOf(o) === target);
  }, [orders, dateFilter]);

  // Exporta o relatório de produção do dia selecionado no filtro — mesmo arquivo
  // gerado na tela de Relatório de Produção (mesmo endpoint, agrupado por cliente).
  const handleExport = useCallback(
    async (format: "pdf" | "xlsx") => {
      const date = resolveFilterDate(dateFilter);
      if (!date) {
        toast.error("Selecione uma data para exportar");
        return;
      }
      setExporting(format);
      try {
        const { filename, blob } = await downloadProductionReportExport(
          { from: date, to: date, customerId: null, groupByCustomer: true },
          format,
        );
        downloadBlob(filename, blob);
      } catch (err) {
        toast.error(getApiErrorMessage(err, "Erro ao exportar"));
      } finally {
        setExporting(null);
      }
    },
    [dateFilter],
  );

  const effectiveSubStatus = (order: OrderWithDecisions): SubStatus =>
    order.phase === "done" ? "PRODUZIDO" : overrides[order.id] ?? subStatusFor(order);

  const grouped = useMemo(() => {
    const out: Record<SubStatus, OrderWithDecisions[]> = {
      PARA_PRODUZIR: [],
      PRODUZINDO: [],
      PRODUZIDO: [],
    };
    for (const order of filteredOrders) {
      out[effectiveSubStatus(order)].push(order);
    }
    return out;
  }, [filteredOrders, overrides]);

  const activeOrder = useMemo(
    () => (activeDragId ? orders.find((o) => o.id === activeDragId) ?? null : null),
    [activeDragId, orders],
  );

  const onOrderChanged = (updated: OrderDetail) => {
    const total = updated.productionList.length;
    const decided = updated.productionList.filter(isDecided).length;
    setOverrides((current) => {
      if (!(updated.id in current)) return current;
      const { [updated.id]: _omit, ...rest } = current;
      return rest;
    });
    setOrders((current) => {
      if (updated.status === "EM_PRODUCAO") {
        const existing = current.find((o) => o.id === updated.id);
        const next = { decided, total, phase: "doing" as const };
        if (existing) return current.map((o) => (o.id === updated.id ? { ...o, ...next } : o));
        return current;
      }
      if (updated.status === "EM_EXPEDICAO") {
        const existing = current.find((o) => o.id === updated.id);
        const next = { decided: 0, total: 0, phase: "done" as const };
        if (existing) return current.map((o) => (o.id === updated.id ? { ...o, ...next } : o));
        return current;
      }
      // Pedido passou pra EM_ROTA+ ou foi cancelado: some do board.
      return current.filter((o) => o.id !== updated.id);
    });
  };

  const handleDragStart = (event: DragStartEvent) => {
    if (readOnly) return;
    setActiveDragId(String(event.active.id));
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    setActiveDragId(null);
    if (readOnly) return;
    if (!event.over) return;
    const orderId = String(event.active.id);
    const droppedOn = String(event.over.id) as SubStatus;
    const order = orders.find((o) => o.id === orderId);
    if (!order) return;
    if (order.phase === "done") return; // Pedido já avançou — não interage.
    const current = effectiveSubStatus(order);
    if (droppedOn === current) return;

    // Drag pra "Produzido" = avançar pra EM_EXPEDICAO. Pede confirmação da data.
    if (droppedOn === "PRODUZIDO") {
      if (order.decided < order.total) {
        toast.info("Decida todos os itens antes de marcar como produzido", {
          action: { label: "Abrir", onClick: () => setSelectedOrderId(orderId) },
        });
        return;
      }
      setAdvanceTarget(order);
      return;
    }

    // Drag entre as 2 primeiras colunas é livre: marca posição manual e segue.
    setOverrides((cur) => ({ ...cur, [orderId]: droppedOn }));
  };

  return (
    <div>
      <header className="bg-gradient-to-br from-primary to-primary/80 text-primary-foreground">
        <div className="container py-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-medium">
                <ChefHat className="h-3.5 w-3.5" />
                Dashboard de produção
              </div>
              <h1 className="mt-3 font-display text-3xl font-bold tracking-tight">Produção em andamento</h1>
              <p className="mt-1 text-primary-foreground/80">
                Pedidos aguardando produção. Arraste para mover entre estados ou clique para decidir item a item.
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="secondary" className="gap-2" onClick={() => void handleExport("pdf")} disabled={exporting !== null}>
                {exporting === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
                PDF
              </Button>
              <Button variant="secondary" className="gap-2" onClick={() => void handleExport("xlsx")} disabled={exporting !== null}>
                {exporting === "xlsx" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
                XLSX
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container py-5">
        {loadState === "loading" && (
          <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Carregando pedidos...
          </div>
        )}

        {loadState === "error" && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
            {error}
          </div>
        )}

        {loadState === "ready" && (
          <>
            <div className="mb-4">
              <KanbanDateFilterBar value={dateFilter} onChange={setDateFilter} showAll={false} label="Produção:" />
            </div>
            <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                {COLUMNS.map((col) => (
                  <KanbanColumn
                    key={col.id}
                    column={col}
                    items={grouped[col.id]}
                    onCardClick={setSelectedOrderId}
                    onLabels={setLabelOrderId}
                    readOnly={readOnly}
                    canLabels={canGenerateLabels}
                  />
                ))}
              </div>
              <DragOverlay>
                {activeOrder && <OrderPedidoCard order={activeOrder} column={COLUMNS[0]} onClick={() => {}} onLabels={() => {}} dragging readOnly={readOnly} canLabels={canGenerateLabels} />}
              </DragOverlay>
            </DndContext>
          </>
        )}
      </div>

      <ProductionOrderDialog
        orderId={selectedOrderId}
        onClose={() => setSelectedOrderId(null)}
        onOrderChanged={onOrderChanged}
      />

      <OrderLabelsDialog open={!!labelOrderId} orderId={labelOrderId} onClose={() => setLabelOrderId(null)} />

      <AdvanceConfirmDialog
        open={advanceTarget != null}
        toStatusLabel="Expedição"
        pedidoLabel={advanceTarget ? orderTitle(advanceTarget) : ""}
        onCancel={() => setAdvanceTarget(null)}
        onConfirm={async (date) => {
          if (!advanceTarget) return;
          try {
            const updated = await transitionOrder(advanceTarget.id, {
              toStatus: "EM_EXPEDICAO",
              transitionDate: date,
            });
            onOrderChanged(updated);
            toast.success("Pedido enviado para expedição");
            setAdvanceTarget(null);
          } catch (err) {
            toast.error(getApiErrorMessage(err, "Não foi possível avançar o pedido"));
          }
        }}
      />
    </div>
  );
}

function KanbanColumn({
  column,
  items,
  onCardClick,
  onLabels,
  readOnly = false,
  canLabels = false,
}: {
  column: ColumnDef;
  items: OrderWithDecisions[];
  onCardClick: (orderId: string) => void;
  onLabels: (orderId: string) => void;
  readOnly?: boolean;
  canLabels?: boolean;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: column.id, disabled: readOnly });
  return (
    <div
      ref={setNodeRef}
      className={cn(
        "rounded-lg bg-secondary/40 p-3 transition-colors min-h-[300px]",
        isOver && "bg-secondary",
      )}
    >
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={cn("h-2 w-2 rounded-full", column.dotColor)} />
          <span className="text-sm font-semibold">{column.label}</span>
        </div>
        <span className="text-xs text-muted-foreground">{items.length}</span>
      </div>
      <div className="flex flex-col gap-2">
        {items.map((order) => (
          <OrderPedidoCard
            key={order.id}
            order={order}
            column={column}
            onClick={() => onCardClick(order.id)}
            onLabels={() => onLabels(order.id)}
            readOnly={readOnly}
            canLabels={canLabels}
          />
        ))}
        {items.length === 0 && (
          <div className="rounded-md border border-dashed border-border/60 p-4 text-center text-xs text-muted-foreground">
            Nenhum pedido
          </div>
        )}
      </div>
    </div>
  );
}

function OrderPedidoCard({
  order,
  column,
  onClick,
  onLabels,
  dragging = false,
  readOnly = false,
  canLabels = false,
}: {
  order: OrderWithDecisions;
  column: ColumnDef;
  onClick: () => void;
  onLabels: () => void;
  dragging?: boolean;
  readOnly?: boolean;
  canLabels?: boolean;
}) {
  const isDone = order.phase === "done";
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: order.id, disabled: isDone || readOnly });
  const preview = order.itemPreview.slice(0, 3).join(", ") + (order.itemPreview.length > 3 ? "..." : "");
  return (
    <div
      ref={setNodeRef}
      onClick={onClick}
      className={cn(
        "group cursor-pointer rounded-md border p-3 transition-all",
        column.cardBg,
        column.cardBorder,
        (isDragging || dragging) && "opacity-50",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate font-display text-sm font-semibold">{order.clientName}</div>
          <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
            <span>{CLIENT_TYPE_LABELS[order.clientType]}</span>
            {isDone && (
              <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
                Em expedição
              </span>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {canLabels && (
            <button
              type="button"
              title="Gerar etiquetas do pedido"
              onClick={(e) => {
                e.stopPropagation();
                onLabels();
              }}
              className="rounded-md p-1 text-muted-foreground opacity-60 transition-opacity hover:bg-secondary hover:text-foreground group-hover:opacity-100"
              aria-label="Gerar etiquetas"
            >
              <Tag className="h-4 w-4" />
            </button>
          )}
          {!isDone && !readOnly && (
            <button
              {...attributes}
              {...listeners}
              onClick={(e) => e.stopPropagation()}
              className="text-muted-foreground opacity-30 transition-opacity group-hover:opacity-100"
              aria-label="Arrastar pedido"
            >
              <GripVertical className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1 whitespace-nowrap" title="Data de produção">
          <Calendar className="h-3 w-3" />
          Produção: {shortBR(productionDateOf(order)) ?? "—"}
        </span>
        <span className="text-muted-foreground/40">·</span>
        <span className="inline-flex items-center gap-1 whitespace-nowrap" title="Data de entrega">
          Entrega: {shortBR(order.deliveryDate) ?? "—"}
        </span>
        {!isDone && (
          <>
            <span className="text-muted-foreground/40">·</span>
            <span className="inline-flex items-center gap-1 whitespace-nowrap">
              <Package className="h-3 w-3" />
              {order.decided}/{order.total} decididos
            </span>
          </>
        )}
      </div>
      {preview && (
        <div className="mt-2 truncate border-t border-border/60 pt-2 text-xs text-muted-foreground">
          {preview}
        </div>
      )}
    </div>
  );
}
